import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { modelCapabilityApi } from '../services/modelCapabilityApi';
import type { ModelCapability, ModelCapabilityMap, ModelCapabilityStatus } from '../types';

export interface ModelCapabilitiesContextValue {
  status: ModelCapabilityStatus;
  capabilities: ModelCapabilityMap;
  retiredModelIds: string[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  get: (model: string) => ModelCapability | undefined;
  listByMediaType: (mediaType: number) => Array<[string, ModelCapability]>;
  getVisibleFields: (model: string) => string[];
}

interface InternalModelCapabilitiesContextValue extends ModelCapabilitiesContextValue {
  ensureLoaded: () => Promise<void>;
}

const ModelCapabilityContext = createContext<InternalModelCapabilitiesContextValue | null>(null);

export function ModelCapabilityProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [capabilities, setCapabilities] = useState<ModelCapabilityMap>({});
  const [retiredModelIds, setRetiredModelIds] = useState<string[]>([]);
  const [status, setStatus] = useState<ModelCapabilityStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const statusRef = useRef<ModelCapabilityStatus>('idle');
  const inFlightRef = useRef<Promise<void> | null>(null);

  const requestCapabilities = useCallback((): Promise<void> => {
    if (inFlightRef.current) return inFlightRef.current;

    statusRef.current = 'loading';
    setStatus('loading');
    setError(null);

    const request = modelCapabilityApi
      .listAll()
      .then((response) => {
        setCapabilities(response.capabilities);
        setRetiredModelIds(response.retired_model_ids ?? []);
        statusRef.current = 'ready';
        setStatus('ready');
      })
      .catch((requestError: unknown) => {
        const message = requestError instanceof Error ? requestError.message : '加载模型能力失败';
        statusRef.current = 'error';
        setStatus('error');
        setError(message);
        console.error('[ModelCapabilityProvider] 加载失败:', requestError);
      })
      .finally(() => {
        if (inFlightRef.current === request) {
          inFlightRef.current = null;
        }
      });

    inFlightRef.current = request;
    return request;
  }, []);

  const ensureLoaded = useCallback((): Promise<void> => {
    if (statusRef.current !== 'idle') {
      return inFlightRef.current ?? Promise.resolve();
    }
    return requestCapabilities();
  }, [requestCapabilities]);

  const reload = useCallback(() => requestCapabilities(), [requestCapabilities]);

  const get = useCallback((model: string) => capabilities[model], [capabilities]);

  const listByMediaType = useCallback(
    (mediaType: number): Array<[string, ModelCapability]> =>
      Object.entries(capabilities).filter(([, capability]) =>
        Array.isArray(capability.mediaType)
          ? capability.mediaType.includes(mediaType)
          : capability.mediaType === mediaType,
      ),
    [capabilities],
  );

  const getVisibleFields = useCallback(
    (model: string): string[] => {
      const capability = capabilities[model];
      if (!capability) return [];
      return capability.fields ?? Object.keys(capability.properties);
    },
    [capabilities],
  );

  const value = useMemo<InternalModelCapabilitiesContextValue>(
    () => ({
      status,
      capabilities,
      retiredModelIds,
      loading: status === 'idle' || status === 'loading',
      error,
      reload,
      get,
      listByMediaType,
      getVisibleFields,
      ensureLoaded,
    }),
    [
      status,
      capabilities,
      retiredModelIds,
      error,
      reload,
      get,
      listByMediaType,
      getVisibleFields,
      ensureLoaded,
    ],
  );

  return (
    <ModelCapabilityContext.Provider value={value}>{children}</ModelCapabilityContext.Provider>
  );
}

export function useModelCapabilities(): ModelCapabilitiesContextValue {
  const context = useContext(ModelCapabilityContext);
  if (!context) {
    throw new Error('useModelCapabilities 必须在 <ModelCapabilityProvider> 内使用');
  }

  const { ensureLoaded } = context;
  useEffect(() => {
    void ensureLoaded();
  }, [ensureLoaded]);

  return context;
}
